import { useEffect, useRef, useState } from "react";
import {
  api,
  type OrchestratorMemory,
  type OrchestratorSession,
} from "../api/client";
import { wsClient } from "../api/ws";

interface Message {
  id: string;
  role: "user" | "assistant" | "tool" | "system";
  content: string;
  ts: number;
  tool?: string;
  toolInput?: unknown;
}

function getApiBase(): string {
  return (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
}
function getToken(): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem("coagent_console_token");
}

const MEMORY_CATEGORIES = ["user_preference", "goal", "fact", "decision"] as const;
type MemoryCategory = (typeof MEMORY_CATEGORIES)[number];

function balanceLabel(b: number): string {
  if (b < 25) return "Только баги";
  if (b < 50) return "В сторону багов";
  if (b === 50) return "Микс 50/50";
  if (b < 75) return "В сторону доработок";
  return "Только доработки";
}

export function Jarvis() {
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [sessions, setSessions] = useState<OrchestratorSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [memories, setMemories] = useState<OrchestratorMemory[]>([]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [showSettings, setShowSettings] = useState(true);
  const [memCategory, setMemCategory] = useState<MemoryCategory>("user_preference");
  const [memInput, setMemInput] = useState("");
  const scrollerRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const reloadConfig = () =>
    api.orchestrator.config().then((r) => setConfig(r.config)).catch(() => {});
  const reloadSessions = () =>
    api.orchestrator.sessions().then((r) => setSessions(r.sessions)).catch(() => {});
  const reloadMemories = () =>
    api.orchestrator.listMemory().then((r) => setMemories(r.memories)).catch(() => {});

  useEffect(() => {
    reloadConfig();
    reloadSessions();
    reloadMemories();
    const unsub = wsClient.subscribe((e) => {
      if (
        e.type === "autopilot.tick" &&
        ((e.payload as { source?: string })?.source === "jarvis-config" ||
          (e.payload as { source?: string })?.source === "jarvis")
      ) {
        reloadConfig();
        reloadSessions();
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight });
  }, [messages]);

  const loadSession = async (id: string) => {
    setActiveSessionId(id);
    try {
      const r = await api.orchestrator.messages(id);
      setMessages(
        r.messages.map((m) => ({
          id: `db-${m.id}`,
          role: m.role,
          content: m.content,
          ts: new Date(m.created_at).getTime(),
        })),
      );
    } catch {
      setMessages([]);
    }
  };

  const newSession = () => {
    setActiveSessionId(null);
    setMessages([]);
  };

  const setKey = async (key: string, value: unknown) => {
    try {
      const r = await api.orchestrator.setConfig(key, value);
      setConfig(r.config);
    } catch (e) {
      alert("Не удалось сохранить настройку: " + String(e));
    }
  };

  const addMemoryNow = async () => {
    if (!memInput.trim()) return;
    try {
      await api.orchestrator.addMemory(memCategory, memInput.trim());
      setMemInput("");
      reloadMemories();
    } catch (e) {
      alert("Не удалось сохранить заметку: " + String(e));
    }
  };

  const removeMemory = async (id: number) => {
    if (!confirm("Удалить заметку?")) return;
    try {
      await api.orchestrator.deleteMemory(id);
      reloadMemories();
    } catch {
      /* ignore */
    }
  };

  const parseSSE = (
    chunk: string,
    onEvent: (type: string, data: Record<string, unknown>) => void,
    state: { buf: string },
  ) => {
    state.buf += chunk;
    const blocks = state.buf.split(/\r?\n\r?\n/);
    state.buf = blocks.pop() ?? "";
    for (const block of blocks) {
      const lines = block.split(/\r?\n/);
      let eventType = "message";
      let dataStr = "";
      for (const ln of lines) {
        const line = ln.trimEnd();
        if (!line) continue;
        if (line.startsWith("event:")) eventType = line.slice(6).trim();
        else if (line.startsWith("data:")) {
          const piece = line.slice(5).replace(/^\s/, "");
          dataStr += (dataStr ? "\n" : "") + piece;
        }
      }
      if (!dataStr) continue;
      try {
        onEvent(eventType, JSON.parse(dataStr));
      } catch {
        /* ignore */
      }
    }
  };

  const send = async () => {
    if (!input.trim() || pending) return;
    const text = input.trim();
    setInput("");
    const userMsgId = `u-${Date.now()}`;
    setMessages((p) => [...p, { id: userMsgId, role: "user", content: text, ts: Date.now() }]);
    setPending(true);

    const assistantMsgId = `a-${Date.now()}`;
    let assistantText = "";
    const ensureAssistant = () => {
      setMessages((prev) => {
        if (prev.find((m) => m.id === assistantMsgId)) return prev;
        return [
          ...prev,
          { id: assistantMsgId, role: "assistant", content: "", ts: Date.now() },
        ];
      });
    };

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const token = getToken();
      const res = await fetch(`${getApiBase()}/orchestrator/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: text, sessionId: activeSessionId ?? undefined }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        const errBody = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${errBody.slice(0, 200)}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      const state = { buf: "" };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        parseSSE(chunk, (type, data) => {
          if (type === "session") {
            const sid = (data as { session_id?: string }).session_id;
            if (sid) setActiveSessionId(sid);
          } else if (type === "text") {
            const t = String((data as { text?: string }).text ?? "");
            if (t) {
              assistantText += t;
              ensureAssistant();
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId ? { ...m, content: assistantText } : m,
                ),
              );
            }
          } else if (type === "tool_use") {
            const toolName = String((data as { tool?: string }).tool ?? "?");
            setMessages((prev) => [
              ...prev,
              {
                id: `tool-${Date.now()}-${Math.random()}`,
                role: "tool",
                content: `🔧 ${toolName}`,
                toolInput: (data as { input?: unknown }).input,
                ts: Date.now(),
                tool: toolName,
              },
            ]);
          } else if (type === "system") {
            const model = (data as { model?: string }).model;
            if (model) {
              setMessages((prev) => [
                ...prev,
                {
                  id: `sys-${Date.now()}`,
                  role: "system",
                  content: `model: ${model}`,
                  ts: Date.now(),
                },
              ]);
            }
          } else if (type === "result") {
            const t = String((data as { text?: string }).text ?? "");
            if (t && !assistantText) {
              assistantText = t;
              ensureAssistant();
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantMsgId ? { ...m, content: t } : m)),
              );
            }
          } else if (type === "error") {
            setMessages((prev) => [
              ...prev,
              {
                id: `err-${Date.now()}`,
                role: "tool",
                content: `Ошибка: ${String((data as { text?: string }).text ?? "")}`,
                ts: Date.now(),
              },
            ]);
          } else if (type === "warn") {
            setMessages((prev) => [
              ...prev,
              {
                id: `warn-${Date.now()}`,
                role: "system",
                content: String((data as { text?: string }).text ?? "warn"),
                ts: Date.now(),
              },
            ]);
          }
        }, state);
      }
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        setMessages((p) => [
          ...p,
          {
            id: `err-${Date.now()}`,
            role: "tool",
            content: `Сетевая ошибка: ${String(err)}`,
            ts: Date.now(),
          },
        ]);
      }
    } finally {
      setPending(false);
      abortRef.current = null;
      reloadSessions();
      reloadMemories();
    }
  };

  const stopGeneration = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setPending(false);
  };

  const autopilotMode = (config.autopilot_mode as string) ?? "off";
  const balance = Number(config.priority_balance ?? 50);
  const intervalSec = Number(config.autopilot_interval_sec ?? 600);
  const batchSize = Number(config.autopilot_batch_size ?? 3);

  return (
    <div className="flex h-full gap-3">
      {/* Left rail — session list */}
      <aside className="hidden w-56 shrink-0 flex-col gap-2 lg:flex">
        <button onClick={newSession} className="btn btn-primary w-full text-sm">
          + Новая сессия
        </button>
        <div className="flex-1 space-y-1 overflow-y-auto">
          {sessions.length === 0 && (
            <div className="text-xs text-panel-muted px-2 py-1">Сессий пока нет.</div>
          )}
          {sessions.map((s) => (
            <button
              key={s.id}
              onClick={() => loadSession(s.id)}
              className={
                "w-full rounded px-2 py-1.5 text-left text-xs " +
                (activeSessionId === s.id
                  ? "bg-panel-border text-panel-fg"
                  : "text-panel-muted hover:bg-panel-card hover:text-panel-fg")
              }
              title={s.title ?? s.id}
            >
              <div className="truncate">{s.title ?? "(без названия)"}</div>
              <div className="text-[10px] opacity-60">
                {new Date(s.last_message_at).toLocaleString()}
                {s.message_count != null ? ` · ${s.message_count}` : ""}
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Main chat */}
      <div className="flex flex-1 flex-col">
        <header className="mb-2 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">🧠 Jarvis</h1>
            <p className="text-xs text-panel-muted">
              Оркестратор: автопилот {autopilotMode === "on" ? "ВКЛ" : "ВЫКЛ"} ·
              приоритет {balance}/100 ({balanceLabel(balance)}) · интервал {intervalSec}s
            </p>
          </div>
          <button onClick={() => setShowSettings((v) => !v)} className="btn text-xs">
            ⚙️ {showSettings ? "Скрыть" : "Настройки"}
          </button>
        </header>

        <div
          ref={scrollerRef}
          className="flex-1 space-y-2 overflow-y-auto rounded border border-panel-border bg-panel-bg p-3"
        >
          {messages.length === 0 && (
            <div className="text-xs text-panel-muted">
              Поздоровайся с Jarvis. Можно: «какие открытые задачи?», «запомни: я предпочитаю
              Codex для тестов», «включи автопилот, балланс 30 (баги вперёд)».
            </div>
          )}
          {messages.map((m) => (
            <div
              key={m.id}
              className={
                "rounded p-2 text-sm " +
                (m.role === "user"
                  ? "bg-panel-border"
                  : m.role === "assistant"
                  ? "bg-panel-card"
                  : m.role === "tool"
                  ? "bg-panel-accent/10 border border-panel-accent/30"
                  : "bg-panel-bg border border-panel-border/50 text-panel-muted")
              }
            >
              <div className="mb-0.5 text-[10px] uppercase text-panel-muted">{m.role}</div>
              <div className="whitespace-pre-wrap break-words">{m.content}</div>
              {m.role === "tool" && m.toolInput != null && (
                <pre className="mt-1 overflow-x-auto rounded bg-panel-bg p-2 text-[10px] text-panel-muted">
                  {JSON.stringify(m.toolInput, null, 2).slice(0, 600)}
                </pre>
              )}
            </div>
          ))}
          {pending && (
            <div className="text-xs text-panel-muted">
              <span className="animate-pulse">◦</span> Jarvis думает…{" "}
              <button onClick={stopGeneration} className="underline hover:text-panel-err">
                прервать
              </button>
            </div>
          )}
        </div>

        <form
          className="mt-3 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Спроси Jarvis…"
            className="flex-1 rounded border border-panel-border bg-panel-bg px-3 py-2 text-sm"
          />
          <button className="btn btn-primary" disabled={pending || !input.trim()}>
            {pending ? "…" : "Отправить"}
          </button>
        </form>
      </div>

      {/* Right rail — settings + memory */}
      {showSettings && (
        <aside className="hidden w-72 shrink-0 flex-col gap-3 overflow-y-auto lg:flex">
          <div className="card space-y-2">
            <div className="text-xs uppercase text-panel-muted">🤖 Автопилот</div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {autopilotMode === "on" ? "ВКЛ" : "ВЫКЛ"}
              </span>
              <button
                onClick={() => setKey("autopilot_mode", autopilotMode === "on" ? "off" : "on")}
                className={
                  "btn text-xs " + (autopilotMode === "on" ? "btn-primary" : "")
                }
              >
                {autopilotMode === "on" ? "Выключить" : "Включить"}
              </button>
            </div>
          </div>

          <div className="card space-y-2">
            <div className="text-xs uppercase text-panel-muted">⚖️ Приоритет</div>
            <div className="text-xs text-panel-muted flex justify-between">
              <span>Баги</span>
              <span>{balance}</span>
              <span>Доработки</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={balance}
              onChange={(e) => setConfig({ ...config, priority_balance: Number(e.target.value) })}
              onMouseUp={(e) =>
                setKey("priority_balance", Number((e.target as HTMLInputElement).value))
              }
              onTouchEnd={(e) =>
                setKey("priority_balance", Number((e.target as HTMLInputElement).value))
              }
              className="w-full"
            />
            <div className="text-xs text-panel-muted text-center">{balanceLabel(balance)}</div>
          </div>

          <div className="card space-y-2">
            <div className="text-xs uppercase text-panel-muted">⏱️ Интервал автопилота</div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={30}
                max={3600}
                value={intervalSec}
                onChange={(e) =>
                  setConfig({ ...config, autopilot_interval_sec: Number(e.target.value) })
                }
                className="w-24 rounded border border-panel-border bg-panel-bg px-2 py-1 text-sm"
              />
              <span className="text-xs text-panel-muted">сек</span>
              <button
                onClick={() => setKey("autopilot_interval_sec", intervalSec)}
                className="btn text-xs"
              >
                Сохранить
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-panel-muted">Размер пачки:</span>
              <input
                type="number"
                min={1}
                max={20}
                value={batchSize}
                onChange={(e) =>
                  setConfig({ ...config, autopilot_batch_size: Number(e.target.value) })
                }
                className="w-16 rounded border border-panel-border bg-panel-bg px-2 py-1 text-sm"
              />
              <button
                onClick={() => setKey("autopilot_batch_size", batchSize)}
                className="btn text-xs"
              >
                OK
              </button>
            </div>
            <button
              onClick={async () => {
                try {
                  await api.orchestrator.runAutopilotNow();
                  alert("Цикл запущен.");
                } catch (e) {
                  alert("Ошибка: " + String(e));
                }
              }}
              className="btn text-xs w-full"
            >
              ⚡ Прогнать цикл сейчас
            </button>
          </div>

          <div className="card space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase text-panel-muted">💾 Заметки</div>
              <span className="text-[10px] text-panel-muted">{memories.length}</span>
            </div>
            <div className="flex gap-1">
              <select
                value={memCategory}
                onChange={(e) => setMemCategory(e.target.value as MemoryCategory)}
                className="rounded border border-panel-border bg-panel-bg px-1 py-1 text-xs"
              >
                {MEMORY_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <input
                value={memInput}
                onChange={(e) => setMemInput(e.target.value)}
                placeholder="новая заметка…"
                className="flex-1 rounded border border-panel-border bg-panel-bg px-2 py-1 text-xs"
              />
              <button onClick={addMemoryNow} className="btn text-xs">
                +
              </button>
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {memories.slice(0, 10).map((m) => (
                <div
                  key={m.id}
                  className="rounded border border-panel-border/50 bg-panel-bg p-1.5 text-xs"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase text-panel-muted">{m.category}</span>
                    <button
                      onClick={() => removeMemory(m.id)}
                      className="text-[10px] text-panel-err hover:underline"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="break-words">{m.content}</div>
                </div>
              ))}
              {memories.length === 0 && (
                <div className="text-xs text-panel-muted">
                  Заметок нет. Скажи Jarvis: «запомни …».
                </div>
              )}
            </div>
          </div>
        </aside>
      )}
    </div>
  );
}
